/**
 * Pure open/closed decision logic for JobDetail's collapsible informational
 * cards (the owner's live-walk finding, 2026-07-05 — "this should be the
 * default position so that everything's collapsed with little 'click me for
 * information'"). Kept framework-free (no React, no DOM at module scope) so
 * it's directly unit-testable, the same house pattern onboardingStep.ts /
 * storyTour.ts / quickStartGuide.ts already establish for "a decision + a
 * best-effort sessionStorage/localStorage persistence half, in one small
 * module a component imports."
 *
 * REUSES THE EXISTING COLLAPSE MECHANISM — this module does not invent a
 * second collapse pattern. JobDetail.tsx already renders "Why this fit" and
 * "About this role" as native <details><summary className={styles.
 * sectionTitle}>; this module only supplies the per-section, per-job DEFAULT
 * (open vs. closed) and the session-remembered override once a user actually
 * taps a <summary> — the <details>/<summary> DOM idiom itself is untouched
 * and is now applied consistently to every informational card on the page,
 * per the brief's "reuse the pattern, don't invent a second one" instruction.
 *
 * SCOPED PER JOB, PER TAB SESSION: sessionStorage (not localStorage) — the
 * brief's own words are "remembers its open/closed state within the
 * session," distinct from onboardingStep.ts's cross-session localStorage
 * persistence. Keyed by jobId so opening "Why this fit" on job A never
 * leaks into job B being pre-opened too.
 */

/** Every informational/explanatory card this page can collapse. The fit
 *  score, the primary action buttons (Tailor / Save to board / the
 *  relocation toggle CONTROL itself / the essay generator / résumé builder),
 *  and the relocation toggle's own on/off row are DELIBERATELY not in this
 *  list — those stay visible by default per the brief ("only the ESSENTIAL
 *  action-forward content stays visible"). This list only names the
 *  heavier informational/explanatory blocks the live walk flagged as walls
 *  of prose. */
export type CollapsibleSectionId =
  | 'relocation-explainer'
  | 'why-this-fit'
  | 'about-this-role'
  | 'what-ill-add'
  | 'how-i-framed-gaps'
  | 'how-this-role-reads';

/** Every section's own DEFAULT state — closed, per the brief's mandate that
 *  collapsed-by-default is now the standing default position, not an
 *  opt-in. A single source of truth so a future new informational card is
 *  added here once rather than each call site guessing its own default. */
export const COLLAPSIBLE_SECTION_DEFAULT: Record<CollapsibleSectionId, boolean> = {
  'relocation-explainer': false,
  'why-this-fit': false,
  'about-this-role': false,
  'what-ill-add': false,
  'how-i-framed-gaps': false,
  // AXIS-P1: the axis-lens "how this role reads" card — collapsed by
  // default per the house mobile doctrine, same as every other
  // informational (non-action) card on this page.
  'how-this-role-reads': false,
};

export type CollapseState = Record<CollapsibleSectionId, boolean>;

/** The honest default snapshot — every section closed. Exported so a fresh
 *  mount (or a storage read failure) has one canonical shape to fall back
 *  to, mirroring onboardingStep.ts's INITIAL_ONBOARDING_PROGRESS precedent. */
export function initialCollapseState(): CollapseState {
  return { ...COLLAPSIBLE_SECTION_DEFAULT };
}

/** Pure toggle — returns a NEW state object with one section flipped, never
 *  mutates the input. Unknown section ids are impossible at the type level,
 *  so no runtime guard is needed here (mirrors advanceOnboardingProgress's
 *  pure-update shape). */
export function toggleSection(state: CollapseState, section: CollapsibleSectionId): CollapseState {
  return { ...state, [section]: !state[section] };
}

// =============================================================================
// sessionStorage persistence — "remembers its open/closed state within the
// session." Mirrors onboardingStep.ts's hasWindow()/try-catch discipline
// exactly: a missing `window` (this repo's actual vitest run — node
// environment, no DOM globals, per vitest.config.ts's own header comment)
// reads as the same honest "nothing persisted yet" as a real storage
// failure (private browsing, storage disabled) — never a thrown
// ReferenceError either way.
// =============================================================================

/** True in any real browser and in a jsdom/happy-dom test environment; false
 *  in this repo's actual vitest run (plain Node, no DOM globals) and any
 *  other non-browser caller. Checked explicitly, same reasoning as
 *  onboardingStep.ts's hasWindow(). */
function hasSessionStorage(): boolean {
  return typeof window !== 'undefined' && typeof window.sessionStorage !== 'undefined';
}

/** Per-job storage key — a DIFFERENT job's collapse choices never bleed into
 *  this one's default render (opening "Why this fit" on job A must not
 *  silently pre-open it on job B). */
function storageKey(jobId: string): string {
  return `throughline:job-detail-collapse:v1:${jobId}`;
}

/** Read this job's remembered collapse state for THIS tab session. Returns
 *  the honest all-closed default on a cold read, a missing/corrupt entry, a
 *  storage-disabled context, or the non-browser (test) environment — never
 *  throws, never invents an open section the user never actually expanded. */
export function readCollapseState(jobId: string): CollapseState {
  if (!hasSessionStorage()) return initialCollapseState();
  try {
    const raw = window.sessionStorage.getItem(storageKey(jobId));
    if (!raw) return initialCollapseState();
    const parsed = JSON.parse(raw) as Partial<Record<string, unknown>>;
    const state = initialCollapseState();
    for (const key of Object.keys(COLLAPSIBLE_SECTION_DEFAULT) as CollapsibleSectionId[]) {
      if (typeof parsed[key] === 'boolean') {
        state[key] = parsed[key] as boolean;
      }
    }
    return state;
  } catch {
    return initialCollapseState();
  }
}

/** Persist this job's collapse state for the rest of THIS tab session.
 *  Best-effort only (same posture as onboardingStep.ts's
 *  writeOnboardingProgress) — a storage failure just means the section
 *  resets to its honest closed default next time, never a thrown error
 *  surfaced to the user over a purely cosmetic preference. */
export function writeCollapseState(jobId: string, state: CollapseState): void {
  if (!hasSessionStorage()) return;
  try {
    window.sessionStorage.setItem(storageKey(jobId), JSON.stringify(state));
  } catch {
    // Best-effort only — see header comment.
  }
}
